import axios from "axios";

interface Selection {
  id: number;
  timeline: string;
  selection: string;
  patientId: number;
  isDefault: boolean;
}

/**
 * Function to update the selection data in the backend
 *
 * @param role - The role of the user ("practitioner" or "patient")
 * @param patientId - The ID of the patient whose selection is being updated
 * @param selectionId - The selection ID associated with the selection
 * @param selection - The selection data to be updated
 * @returns Updated selection data or throws an error if the request fails
 */
export async function updateSelection(
  role: "practitioner" | "patient",
  selectionId: string,
  selection: { [key: string]: string[] },
  patientId?: string,
): Promise<Selection | undefined> {
  try {
    // Prepare the data to send in the request
    const adaptedData = {
      selection: JSON.stringify(selection),
      isDefault: selection.isDefault,
    };

    let apiUrl: string;
    if (role === "practitioner") {
      if (!patientId) {
        throw new Error("Patient ID is required for practitioners.");
      }
      apiUrl = `/practitioner/visualfield/${patientId}/${selectionId}/selection`;
    } else {
      apiUrl = `/patient/visualfield/${selectionId}/selection`;
    }

    // Make the PUT request to update the selection
    const response = await axios.put(
      apiUrl,
      adaptedData
    );

    // Return the updated selection data
    return response.data;
  } catch (error: unknown) {  // Use 'unknown' instead of 'any'
    // Log the error for debugging purposes
    console.error("Error while updating the selection:", error);

    if (axios.isAxiosError(error)) {
      // Handle Axios-specific error
      if (error.response) {
        // Server responded with a status code outside of the 2xx range
        console.error("Response error data:", error.response.data);
        throw new Error(
          `Failed to update selection: ${error.response.data?.message || 'Unknown error'}`
        );
      } else if (error.request) {
        // Request was made, but no response was received (network issues, etc.)
        console.error("No response received:", error.request);
        throw new Error("No response received from the server. Please try again.");
      }
    } else if (error instanceof Error) {
      // Handle other types of errors (e.g., client-side issues)
      console.error("Error message:", error.message);
      throw new Error(`Error: ${error.message}`);
    } else {
      // Handle unknown types of errors
      throw new Error("An unknown error occurred.");
    }
  }
}
